import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Save, Shirt, Check } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { getStyleCode } from "@/data/styleCodes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Jacket Styling Options
const JACKET_BODY_STYLES = ["Slim", "Regular", "Classic", "Relaxed"];
const JACKET_FRONT_CLOSURES = [
  "1-Button", "2-Button", "3-Button", "3-Roll-2", "4-Button",
  "2x1 DB", "4x2 DB", "6x1 DB", "6x2 DB", "8x2 DB"
];
const LAPEL_TYPES = ["Notch", "Peak", "Shawl"];
const LAPEL_WIDTHS = [
  "2.5\" (6.4cm)", 
  "3\" (7.6cm)", 
  "3.5\" (8.9cm)", 
  "3.75\" (9.5cm)", 
  "4\" (10.2cm)", 
  "4.5\" (11.4cm)", 
  "5\" (12.7cm)", 
  "5.5\" (14cm)", 
  "6\" (15.2cm)"
];
const EXTERIOR_STITCHES = ["None", "1/16\" Pic", "5/16\" Pic", "1/16\" Top", "5/16\" Top", "1/4\" Double"];
const WAIST_POCKET_ANGLES = ["Straight", "Slanted"];
const TICKET_POCKETS = ["None", "Left Side", "Right Side"];
const CHEST_POCKETS = ["None", "Welt", "Patch"];
const BUTTON_STYLES = ["Horn", "Corozo", "Mother of Pearl", "Metal"];
const SLEEVE_BUTTONHOLES = ["4 Kissing", "4 Non-Kissing", "3 Kissing", "3 Non-Kissing", "No Buttons"];
const SLEEVE_BUTTON_STYLES = ["Same as Front", "Contrasting"];
const UNDER_COLLAR_FELTS = ["Standard", "Premium Melton"];
const LAPEL_BUTTONHOLES = ["None", "Working Buttonhole", "Milanese Hole", "Keyhole"];
const VENTS = ["Single Vent", "Double Vent", "No Vent"];
const FRENCH_FACINGS = ["None", "Standard", "Extended", "Full"];

// Pants Styling Options
const PANT_BODY_STYLES = ["Slim", "Regular", "Classic", "Relaxed"];
const PANT_FRONT_STYLES = ["Flat Front", "Single Pleat", "Double Pleat"];
const FRONT_POCKETS = ["Slant", "On-Seam", "Western"];
const BACK_POCKET_CLOSURES = ["Button", "No Button"];
const BOTTOM_FINISHINGS = ["Plain Hem", "Cuff 1.5\"", "Cuff 1.75\"", "Cuff 2\""];
const BACK_POCKET_STYLES = ["Double Welt", "Single Welt", "Patch"];
const FRONT_CLOSURES_PANT = ["Button & Tab", "Hook & Bar", "Extended Tab"];
const WAISTBAND_STYLES = ["Standard", "Extended", "Side Tabs"];
const WATCH_POCKETS = ["Yes", "No"];
const SUSPENDER_BUTTONS = ["Yes", "No"];

// Vest Styling Options
const VEST_LAPELS = ["None", "Notch", "Peak", "Shawl"];
const VEST_FRONT_CLOSURES = ["5-Button", "6-Button", "4-Button"];
const VEST_POCKET_TYPES = ["Welt", "Patch", "No Pockets"];
const VEST_POCKET_NUMBERS = ["2 Pockets", "4 Pockets"];
const VEST_BACKS = ["Same Fabric", "Lining", "Belt Back"];

// Style Select Component (renders RC factory code beside option name)
const StyleSelect = ({ label, value, onChange, options, codePrefix }) => {
  return (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-background border-white/10 h-8 text-xs">
          <SelectValue>
            <span className="flex items-center gap-1.5 truncate">
              <span className="truncate">{value}</span>
              {(() => {
                const c = getStyleCode(codePrefix, options, value);
                return c ? <code className="text-[10px] text-amber-400/80 font-mono shrink-0">{c}</code> : null;
              })()}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => {
            const c = getStyleCode(codePrefix, options, opt);
            return (
              <SelectItem key={opt} value={opt} className="text-xs">
                <span className="flex items-center gap-2 w-full">
                  <span className="flex-1">{opt}</span>
                  {c && <code className="text-[10px] text-amber-400/80 font-mono">{c}</code>}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

const AdminMTMStylesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order");
  const garmentType = searchParams.get("garment") || "jacket";
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [activeTab, setActiveTab] = useState(garmentType);

  // Jacket Styling State
  const [jacketStyling, setJacketStyling] = useState({
    body_style: "Regular", front_closure: "2-Button", lapel_type: "Notch",
    lapel_width: "3\" (7.6cm)", exterior_stitch: "None", waist_pocket_angle: "Straight",
    ticket_pocket: "None", chest_pocket: "Welt", button_style: "Horn",
    sleeve_buttonholes: "4 Kissing", sleeve_button_style: "Same as Front",
    under_collar_felt: "Standard", lapel_buttonhole: "None", vent: "Double Vent",
    french_facing: "None", under_collar_monogram: ""
  });
  const [jacketStylingRemarks, setJacketStylingRemarks] = useState("");

  // Pant Styling State
  const [pantStyling, setPantStyling] = useState({
    body_style: "Regular", front_style: "Flat Front", front_pocket: "Slant",
    back_pocket_closure: "Button", bottom_finishing: "Plain Hem",
    back_pocket_style: "Double Welt", front_closure: "Button & Tab",
    waistband_style: "Standard", watch_pocket: "No", suspender_buttons: "No"
  });
  const [pantStylingRemarks, setPantStylingRemarks] = useState("");

  // Vest Styling State
  const [vestStyling, setVestStyling] = useState({
    lapel: "None", front_closure: "5-Button", pocket_type: "Welt",
    pocket_number: "4 Pockets", back: "Lining"
  });
  const [vestStylingRemarks, setVestStylingRemarks] = useState("");

  // Load existing order styles if editing
  useEffect(() => {
    if (orderId) {
      loadOrderStyles();
    }
  }, [orderId]);

  const loadOrderStyles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/mtm-orders/${orderId}`);
      const order = response.data;
      setOrderData(order);
      
      if (order.jacket_measurements?.styling) {
        setJacketStyling(order.jacket_measurements.styling);
        setJacketStylingRemarks(order.jacket_measurements.styling_remarks || "");
      }
      if (order.pant_measurements?.styling) {
        setPantStyling(order.pant_measurements.styling);
        setPantStylingRemarks(order.pant_measurements.styling_remarks || "");
      }
      if (order.vest_measurements?.styling) {
        setVestStyling(order.vest_measurements.styling);
        setVestStylingRemarks(order.vest_measurements.styling_remarks || "");
      }
    } catch (error) {
      console.error("Error loading order:", error);
      toast.error("Failed to load order styles");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (orderId && orderData) {
        // Update existing order with new styles
        const updateData = {
          ...orderData,
          jacket_measurements: orderData.jacket_measurements ? {
            ...orderData.jacket_measurements,
            styling: jacketStyling,
            styling_remarks: jacketStylingRemarks
          } : null,
          pant_measurements: orderData.pant_measurements ? {
            ...orderData.pant_measurements,
            styling: pantStyling,
            styling_remarks: pantStylingRemarks
          } : null,
          vest_measurements: orderData.vest_measurements ? {
            ...orderData.vest_measurements,
            styling: vestStyling,
            styling_remarks: vestStylingRemarks
          } : null
        };
        
        await axios.put(`${API}/mtm-orders/${orderId}`, updateData);
        toast.success("Styles saved successfully!");
      } else {
        // Save to localStorage for new orders
        const stylesData = {
          jacket: { styling: jacketStyling, styling_remarks: jacketStylingRemarks },
          pant: { styling: pantStyling, styling_remarks: pantStylingRemarks },
          vest: { styling: vestStyling, styling_remarks: vestStylingRemarks }
        };
        localStorage.setItem("mtm_styles", JSON.stringify(stylesData));
        toast.success("Styles saved! Return to MTM Order to complete.");
      }
    } catch (error) {
      console.error("Error saving styles:", error);
      toast.error("Failed to save styles");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: "jacket", label: "Jacket", icon: Shirt },
    { id: "pant", label: "Trousers", icon: Shirt },
    { id: "vest", label: "Vest", icon: Shirt }
  ];

  return (
    <div className="min-h-screen pt-20 pb-16">
      {/* Step Navigation */}
      <section className="sticky top-16 z-50 bg-background/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-1 overflow-x-auto py-2">
            {[
              { id: 1, label: "GENERAL INFO", href: "/admin/mtm-order" },
              { id: 2, label: "FABRIC & LINING", href: "/admin/mtm-fabric" },
              { id: 3, label: "MEASUREMENTS", href: "/admin/mtm-measurements" },
              { id: 4, label: "STYLES", href: "/admin/mtm-styles", active: true },
              { id: 5, label: "SUBMIT", href: "/admin/mtm-submit" },
            ].map((step) => (
              <button
                key={step.id}
                onClick={() => navigate(step.href)}
                className={`
                  relative flex items-center gap-2 px-4 py-2 text-xs font-medium whitespace-nowrap transition-all
                  ${step.active 
                    ? "bg-primary text-primary-foreground" 
                    : step.id < 4 
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-slate-700 text-white hover:bg-slate-600"
                  }
                `}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step.id < 4 ? 'bg-white/30' : 'bg-white/20'}`}>
                  {step.id < 4 ? <Check size={12} /> : step.id}
                </span>
                {step.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Header */}
      <section className="py-6 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Button variant="ghost" onClick={() => navigate("/admin/mtm-order")} className="mb-4">
            <ArrowLeft size={16} className="mr-2" /> Back to MTM Order
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl tracking-tight">MTM Styles</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Configure styling options for your made-to-measure garments
                {orderId && orderData && (
                  <span className="text-primary ml-2">• Order #{orderData.order_number}</span>
                )}
              </p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-primary">
              {saving ? "Saving..." : <><Save size={16} className="mr-2" /> Save Styles</>}
            </Button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id 
                    ? "border-primary text-primary" 
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Jacket Styling */}
          {activeTab === "jacket" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-blue-700 text-white px-4 py-3 font-medium">JACKET STYLING</div>
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    <StyleSelect label="Body Style" value={jacketStyling.body_style} onChange={(v) => setJacketStyling(p => ({...p, body_style: v}))} options={JACKET_BODY_STYLES} />
                    <StyleSelect label="Front Closure" value={jacketStyling.front_closure} onChange={(v) => setJacketStyling(p => ({...p, front_closure: v}))} options={JACKET_FRONT_CLOSURES} codePrefix="front_closure" />
                    <StyleSelect label="Lapel Type" value={jacketStyling.lapel_type} onChange={(v) => setJacketStyling(p => ({...p, lapel_type: v}))} options={LAPEL_TYPES} codePrefix="lapel_type" />
                    <StyleSelect label="Lapel Width" value={jacketStyling.lapel_width} onChange={(v) => setJacketStyling(p => ({...p, lapel_width: v}))} options={LAPEL_WIDTHS} codePrefix="lapel_width" />
                    <StyleSelect label="Exterior Stitch" value={jacketStyling.exterior_stitch} onChange={(v) => setJacketStyling(p => ({...p, exterior_stitch: v}))} options={EXTERIOR_STITCHES} />
                    <StyleSelect label="Waist Pocket Angle" value={jacketStyling.waist_pocket_angle} onChange={(v) => setJacketStyling(p => ({...p, waist_pocket_angle: v}))} options={WAIST_POCKET_ANGLES} codePrefix="waist_pocket_angle" />
                    <StyleSelect label="Ticket Pocket" value={jacketStyling.ticket_pocket} onChange={(v) => setJacketStyling(p => ({...p, ticket_pocket: v}))} options={TICKET_POCKETS} codePrefix="ticket_pocket" />
                    <StyleSelect label="Chest Pocket" value={jacketStyling.chest_pocket} onChange={(v) => setJacketStyling(p => ({...p, chest_pocket: v}))} options={CHEST_POCKETS} codePrefix="chest_pocket" />
                    <StyleSelect label="Button Style" value={jacketStyling.button_style} onChange={(v) => setJacketStyling(p => ({...p, button_style: v}))} options={BUTTON_STYLES} />
                    <StyleSelect label="Sleeve Buttonholes" value={jacketStyling.sleeve_buttonholes} onChange={(v) => setJacketStyling(p => ({...p, sleeve_buttonholes: v}))} options={SLEEVE_BUTTONHOLES} />
                    <StyleSelect label="Sleeve Button Style" value={jacketStyling.sleeve_button_style} onChange={(v) => setJacketStyling(p => ({...p, sleeve_button_style: v}))} options={SLEEVE_BUTTON_STYLES} />
                    <StyleSelect label="Under Collar Felt" value={jacketStyling.under_collar_felt} onChange={(v) => setJacketStyling(p => ({...p, under_collar_felt: v}))} options={UNDER_COLLAR_FELTS} />
                    <StyleSelect label="Lapel Buttonhole" value={jacketStyling.lapel_buttonhole} onChange={(v) => setJacketStyling(p => ({...p, lapel_buttonhole: v}))} options={LAPEL_BUTTONHOLES} codePrefix="lapel_buttonhole" />
                    <StyleSelect label="Vent" value={jacketStyling.vent} onChange={(v) => setJacketStyling(p => ({...p, vent: v}))} options={VENTS} codePrefix="vent" />
                    <StyleSelect label="French Facing" value={jacketStyling.french_facing} onChange={(v) => setJacketStyling(p => ({...p, french_facing: v}))} options={FRENCH_FACINGS} />
                  </div>

                  {/* Remarks */}
                  <div className="mt-6">
                    <div className="bg-gray-700 text-white text-sm py-2 px-4 font-medium rounded-t">JACKET STYLING REMARKS</div>
                    <Textarea
                      value={jacketStylingRemarks}
                      onChange={(e) => setJacketStylingRemarks(e.target.value)}
                      placeholder="Additional notes for jacket styling..."
                      className="bg-background border-white/10 rounded-t-none min-h-[100px]"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Pant Styling */}
          {activeTab === "pant" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-red-700 text-white px-4 py-3 font-medium">TROUSER STYLING</div>
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    <StyleSelect label="Body Style" value={pantStyling.body_style} onChange={(v) => setPantStyling(p => ({...p, body_style: v}))} options={PANT_BODY_STYLES} codePrefix="pant_body_style" />
                    <StyleSelect label="Front Style" value={pantStyling.front_style} onChange={(v) => setPantStyling(p => ({...p, front_style: v}))} options={PANT_FRONT_STYLES} codePrefix="pant_front_style" />
                    <StyleSelect label="Front Pocket" value={pantStyling.front_pocket} onChange={(v) => setPantStyling(p => ({...p, front_pocket: v}))} options={FRONT_POCKETS} codePrefix="pant_front_pocket" />
                    <StyleSelect label="Back Pocket Closure" value={pantStyling.back_pocket_closure} onChange={(v) => setPantStyling(p => ({...p, back_pocket_closure: v}))} options={BACK_POCKET_CLOSURES} codePrefix="pant_back_pocket_closure" />
                    <StyleSelect label="Bottom Finishing" value={pantStyling.bottom_finishing} onChange={(v) => setPantStyling(p => ({...p, bottom_finishing: v}))} options={BOTTOM_FINISHINGS} codePrefix="pant_bottom_finishing" />
                    <StyleSelect label="Back Pocket Style" value={pantStyling.back_pocket_style} onChange={(v) => setPantStyling(p => ({...p, back_pocket_style: v}))} options={BACK_POCKET_STYLES} codePrefix="pant_back_pocket_style" />
                    <StyleSelect label="Front Closure" value={pantStyling.front_closure} onChange={(v) => setPantStyling(p => ({...p, front_closure: v}))} options={FRONT_CLOSURES_PANT} codePrefix="pant_front_closure" />
                    <StyleSelect label="Waistband Style" value={pantStyling.waistband_style} onChange={(v) => setPantStyling(p => ({...p, waistband_style: v}))} options={WAISTBAND_STYLES} codePrefix="pant_waistband_style" />
                    <StyleSelect label="Watch Pocket" value={pantStyling.watch_pocket} onChange={(v) => setPantStyling(p => ({...p, watch_pocket: v}))} options={WATCH_POCKETS} />
                    <StyleSelect label="Suspender Buttons" value={pantStyling.suspender_buttons} onChange={(v) => setPantStyling(p => ({...p, suspender_buttons: v}))} options={SUSPENDER_BUTTONS} />
                  </div>

                  {/* Remarks */}
                  <div className="mt-6">
                    <div className="bg-gray-700 text-white text-sm py-2 px-4 font-medium rounded-t">TROUSER STYLING REMARKS</div>
                    <Textarea
                      value={pantStylingRemarks}
                      onChange={(e) => setPantStylingRemarks(e.target.value)}
                      placeholder="Additional notes for trouser styling..."
                      className="bg-background border-white/10 rounded-t-none min-h-[100px]"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Vest Styling */}
          {activeTab === "vest" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-purple-700 text-white px-4 py-3 font-medium">VEST STYLING</div>
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    <StyleSelect label="Lapel" value={vestStyling.lapel} onChange={(v) => setVestStyling(p => ({...p, lapel: v}))} options={VEST_LAPELS} codePrefix="vest_lapel" />
                    <StyleSelect label="Front Closure" value={vestStyling.front_closure} onChange={(v) => setVestStyling(p => ({...p, front_closure: v}))} options={VEST_FRONT_CLOSURES} codePrefix="vest_front_closure" />
                    <StyleSelect label="Pocket Type" value={vestStyling.pocket_type} onChange={(v) => setVestStyling(p => ({...p, pocket_type: v}))} options={VEST_POCKET_TYPES} codePrefix="vest_pocket_type" />
                    <StyleSelect label="Pocket Number" value={vestStyling.pocket_number} onChange={(v) => setVestStyling(p => ({...p, pocket_number: v}))} options={VEST_POCKET_NUMBERS} codePrefix="vest_pocket_number" />
                    <StyleSelect label="Back" value={vestStyling.back} onChange={(v) => setVestStyling(p => ({...p, back: v}))} options={VEST_BACKS} codePrefix="vest_back" />
                  </div>

                  {/* Remarks */}
                  <div className="mt-6">
                    <div className="bg-gray-700 text-white text-sm py-2 px-4 font-medium rounded-t">VEST STYLING REMARKS</div>
                    <Textarea
                      value={vestStylingRemarks}
                      onChange={(e) => setVestStylingRemarks(e.target.value)}
                      placeholder="Additional notes for vest styling..."
                      className="bg-background border-white/10 rounded-t-none min-h-[100px]"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Save Button (bottom) */}
          <div className="mt-8 flex justify-end">
            <Button onClick={handleSave} disabled={saving} size="lg" className="bg-primary px-8">
              {saving ? "Saving..." : <><Save size={18} className="mr-2" /> Save All Styles</>}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminMTMStylesPage;
